/**
 * Configurações › Vagas do site - the framework-neutral, tested view-model of the institutional site's job openings: the editable
 * form, its validation with the SAME rules the server applies (it calls the `@vello/careers` kernel directly - bounds, `https://` /
 * `mailto:` only), the payload the API takes, where an opening is (a city of the registry, or the whole country) and what this
 * operator may do from its grants.
 *
 * The console only FILTERS and HELPS: the server re-validates and re-authorizes every call (`career_opening:view` to read,
 * `career_opening:manage` to write, both at the opening's COUNTRY). Neutral: no country, city, language or regime is written here.
 */
import type { CareerOpening, CareerOpeningContent, CareerOpeningStatus, OperatingMarketView } from '@vello/api-client';
import {
  CAREER_APPLY_URL_MAX,
  CAREER_DESCRIPTION_MAX,
  CAREER_EMPLOYMENT_TYPE_MAX,
  CAREER_SUMMARY_MAX,
  CAREER_TEAM_MAX,
  CAREER_TITLE_MAX,
  validateCareerContent,
} from '@vello/careers';
import { grantsAllow, type KnownGrants } from './console-access.js';

export const CAREER_FORM_LIMITS = {
  title: CAREER_TITLE_MAX,
  team: CAREER_TEAM_MAX,
  employmentType: CAREER_EMPLOYMENT_TYPE_MAX,
  summary: CAREER_SUMMARY_MAX,
  description: CAREER_DESCRIPTION_MAX,
  applyUrl: CAREER_APPLY_URL_MAX,
} as const;

/** The form's fields as typed: a city of '' means the whole country, a link of '' means none (candidates use the Central chat). */
export interface CareerForm {
  readonly country: string;
  readonly marketId: string;
  readonly title: string;
  readonly team: string;
  readonly employmentType: string;
  readonly summary: string;
  readonly description: string;
  readonly applyUrl: string;
}

export function emptyCareerForm(country: string, marketId = ''): CareerForm {
  return { country, marketId, title: '', team: '', employmentType: '', summary: '', description: '', applyUrl: '' };
}

export function careerFormFromOpening(o: CareerOpening): CareerForm {
  return { country: o.country, marketId: o.marketId ?? '', title: o.title, team: o.team, employmentType: o.employmentType, summary: o.summary, description: o.description, applyUrl: o.applyUrl ?? '' };
}

/** The words the API takes (an empty city or link is left out). */
export function careerContentOf(form: CareerForm): CareerOpeningContent {
  return {
    ...(form.marketId.trim() !== '' ? { marketId: form.marketId.trim() } : {}),
    title: form.title, team: form.team, employmentType: form.employmentType, summary: form.summary, description: form.description,
    ...(form.applyUrl.trim() !== '' ? { applyUrl: form.applyUrl.trim() } : {}),
  };
}

/** Whether the form can be sent: the kernel accepts every word (the server decides again). */
export function careerFormValid(form: CareerForm): boolean {
  return /^[A-Z]{2}$/.test(form.country) && validateCareerContent(careerContentOf(form)) !== undefined;
}

/** The link field is the one with its own rule the person can get wrong: https:// or mailto: only. */
export function careerLinkInvalid(applyUrl: string): boolean {
  return applyUrl.trim() !== '' && validateCareerContent({ title: 'xxx', team: 'x', employmentType: 'x', summary: 'x', description: 'x', applyUrl }) === undefined;
}

/** The registered cities of one country, by name (the form's city list). */
export function marketsOfCountry(markets: readonly OperatingMarketView[], country: string): readonly OperatingMarketView[] {
  return markets.filter((m) => m.countryCode.toUpperCase() === country.toUpperCase()).sort((a, b) => a.name.localeCompare(b.name));
}

/** The city an opening names (its registry name; the id when the registry no longer lists it), or undefined for the whole country. */
export function careerCityName(o: Pick<CareerOpening, 'marketId'>, markets: readonly OperatingMarketView[]): string | undefined {
  return o.marketId === undefined ? undefined : (markets.find((m) => m.id === o.marketId)?.name ?? o.marketId);
}

/**
 * The openings a scope shows: with a city selected, that city's openings and the country-wide ones (they apply to it); otherwise all.
 * Newest first (the server's order is kept; ties keep their order).
 */
export function openingsInScope(openings: readonly CareerOpening[], city: string | undefined): readonly CareerOpening[] {
  const shown = city === undefined ? openings : openings.filter((o) => o.marketId === undefined || o.marketId === city);
  return [...shown].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** The statuses an opening can move to from its current one (any other; the same status twice is refused by the server). */
export function careerNextStatuses(from: CareerOpeningStatus): readonly CareerOpeningStatus[] {
  return (['OPEN', 'CLOSED', 'DRAFT'] as const).filter((s) => s !== from && !(from === 'DRAFT' && s === 'CLOSED') && !(from === 'CLOSED' && s === 'OPEN'));
}

/** What this operator may do (unknown grants -> the server decides). */
export function careerAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: true, mayManage: true };
  return {
    mayView: grantsAllow(grants, { permission: 'career_opening:view', target: 'COUNTRY' }),
    mayManage: grantsAllow(grants, { permission: 'career_opening:manage', target: 'COUNTRY' }),
  };
}
